/**
 * App — root component with routing and auth provider
 */
import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import AuthProvider from './auth/AuthContext';
import ProtectedRoute from './auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import { ToastProvider } from './components/common/ToastContext';

/** Retry a dynamic import once by reloading the page on chunk load failure */
const lazyRetry = (importFn) =>
  lazy(() =>
    importFn().catch(() => {
      const hasReloaded = sessionStorage.getItem('chunk_reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk_reload', '1');
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem('chunk_reload');
      return importFn(); // retry once more, let it fail naturally if still broken
    })
  );
import AppShell from './components/layout/AppShell';
import OfflineBanner from './components/common/OfflineBanner';
import UpdatePrompt from './components/common/UpdatePrompt';
import DevRoleSwitcher from './components/common/DevRoleSwitcher';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import useTheme from './hooks/useTheme';
import BadgeToastListener from './components/common/BadgeToastListener';
import CelebrationListener from './components/common/CelebrationListener';
import { NavigationDirectionProvider } from './hooks/useNavigationDirection';
const PushNotificationListener = lazy(() => import('./components/common/PushNotificationListener'));

// Public pages
const LoginPage = lazyRetry(() => import('./pages/onboarding/LoginPage'));
const AcceptInvitePage = lazyRetry(() => import('./pages/onboarding/AcceptInvitePage'));
const RegisterPage = lazyRetry(() => import('./pages/onboarding/RegisterPage'));
const SignUpPage = lazyRetry(() => import('./pages/onboarding/SignUpPage'));
const NotFoundPage = lazyRetry(() => import('./pages/NotFoundPage'));

// Client pages
const DashboardPage = lazyRetry(() => import('./pages/client/DashboardPage'));
const PetProfilePage = lazyRetry(() => import('./pages/client/PetProfilePage'));
const PetProfileSetupPage = lazyRetry(() => import('./pages/onboarding/PetProfileSetupPage'));
const ProgramPage = lazyRetry(() => import('./pages/client/ProgramPage'));
const SkillDetailPage = lazyRetry(() => import('./pages/client/SkillDetailPage'));
const CheckInPage = lazyRetry(() => import('./pages/client/CheckInPage'));
const CommunicationPage = lazyRetry(() => import('./pages/client/CommunicationPage'));
const SettingsPage = lazyRetry(() => import('./pages/client/SettingsPage'));

// Trainer pages
const TrainerDashboardPage = lazyRetry(() => import('./pages/trainer/TrainerDashboardPage'));
const ClientListPage = lazyRetry(() => import('./pages/trainer/ClientListPage'));
const ClientDetailPage = lazyRetry(() => import('./pages/trainer/ClientDetailPage'));
const AssignHomeworkPage = lazyRetry(() => import('./pages/trainer/AssignHomeworkPage'));
const ReviewCheckInsPage = lazyRetry(() => import('./pages/trainer/ReviewCheckInsPage'));
const InvitePage = lazyRetry(() => import('./pages/trainer/InvitePage'));
const TrainerEnrollmentDetailPage = lazyRetry(() => import('./pages/trainer/TrainerEnrollmentDetailPage'));
const TrainerSkillDetailPage = lazyRetry(() => import('./pages/trainer/TrainerSkillDetailPage'));
const TrainerPetDashboardPage = lazyRetry(() => import('./pages/trainer/PetDashboardPage'));
const ClientPetDashboardPage = lazyRetry(() => import('./pages/client/PetDashboardPage'));
const BadgesPage = lazyRetry(() => import('./pages/client/BadgesPage'));
const ModulePage = lazyRetry(() => import('./pages/client/ModulePage'));
const LessonPage = lazyRetry(() => import('./pages/client/LessonPage'));
const ConversationListPage = lazyRetry(() => import('./pages/trainer/ConversationListPage'));
const TrainerConversationPage = lazyRetry(() => import('./pages/trainer/TrainerConversationPage'));
const AssignModulePage = lazyRetry(() => import('./pages/trainer/AssignModulePage'));
const TrainerModuleDetailPage = lazyRetry(() => import('./pages/trainer/TrainerModuleDetailPage'));
const TrainerLessonPage = lazyRetry(() => import('./pages/trainer/TrainerLessonPage'));
const PetsPage = lazyRetry(() => import('./pages/client/PetsPage'));
const AddPetPage = lazyRetry(() => import('./pages/client/AddPetPage'));
const ProgramsStorePage = lazyRetry(() => import('./pages/client/ProgramsStorePage'));
const ProgramDetailPage = lazyRetry(() => import('./pages/client/ProgramDetailPage'));
const MyProgramsPage = lazyRetry(() => import('./pages/client/MyProgramsPage'));
const PurchaseSuccessPage = lazyRetry(() => import('./pages/client/PurchaseSuccessPage'));
const TrainerAddPetPage = lazyRetry(() => import('./pages/trainer/AddPetPage'));
const ContentLibraryPage = lazyRetry(() => import('./pages/trainer/ContentLibraryPage'));
const ProgramEditorPage = lazyRetry(() => import('./pages/trainer/ProgramEditorPage'));
const SkillEditorPage = lazyRetry(() => import('./pages/trainer/SkillEditorPage'));
const ModuleEditorPage = lazyRetry(() => import('./pages/trainer/ModuleEditorPage'));
const LessonEditorPage = lazyRetry(() => import('./pages/trainer/LessonEditorPage'));
const ProgramPreviewPage = lazyRetry(() => import('./pages/trainer/ProgramPreviewPage'));
const SkillPreviewPage = lazyRetry(() => import('./pages/trainer/SkillPreviewPage'));
const ModulePreviewPage = lazyRetry(() => import('./pages/trainer/ModulePreviewPage'));
const LessonPreviewPage = lazyRetry(() => import('./pages/trainer/LessonPreviewPage'));

const DarkModePrompt = () => {
  const { showDarkPrompt, dismissDarkPrompt, switchToLight } = useTheme();
  if (!showDarkPrompt) return null;
  return (
    <div className="dark-mode-prompt">
      <p>Dark mode was detected from your device settings.</p>
      <div className="dark-mode-prompt__actions">
        <button className="button button--primary button--small" onClick={switchToLight}>
          Switch to Light Mode
        </button>
        <button className="button button--outline button--small" onClick={dismissDarkPrompt}>
          Keep Dark Mode
        </button>
      </div>
    </div>
  );
};

const App = () => {
  useTheme();
  return (
    <ErrorBoundary>
      <Router>
        <NavigationDirectionProvider>
        <ToastProvider>
        <BadgeToastListener />
        <CelebrationListener />
        <DarkModePrompt />
        <AuthProvider>
          <Suspense fallback={null}><PushNotificationListener /></Suspense>
          <OfflineBanner />
          <UpdatePrompt />
          <DevRoleSwitcher />
          <Suspense fallback={<LoadingSpinner fullPage />}>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/invite/:inviteId" element={<AcceptInvitePage />} />
              <Route path="/register/:inviteId" element={<RegisterPage />} />
              <Route path="/signup" element={
                <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY} useEnterprise>
                  <SignUpPage />
                </GoogleReCaptchaProvider>
              } />

              {/* Client routes */}
              <Route element={<ProtectedRoute requiredRole="client"><AppShell /></ProtectedRoute>}>
                <Route path="/" element={<DashboardPage />} />
                <Route path="/pets" element={<PetsPage />} />
                <Route path="/pets/new" element={<AddPetPage />} />
                <Route path="/pets/:petId" element={<ClientPetDashboardPage />} />
                <Route path="/pets/:petId/profile" element={<PetProfilePage />} />
                <Route path="/my-programs" element={<MyProgramsPage />} />
                <Route path="/shop" element={<ProgramsStorePage />} />
                <Route path="/shop/:programId" element={<ProgramDetailPage />} />
                <Route path="/programs" element={<Navigate to="/shop" replace />} />
                <Route path="/programs/:enrollmentId" element={<ProgramPage />} />
                <Route path="/programs/:enrollmentId/skills/:skillId" element={<SkillDetailPage />} />
                <Route path="/badges" element={<BadgesPage />} />
                <Route path="/modules/:assignmentId" element={<ModulePage />} />
                <Route path="/modules/:assignmentId/lessons/:lessonId" element={<LessonPage />} />
                <Route path="/check-in/:enrollmentId" element={<CheckInPage />} />
                <Route path="/connect" element={<CommunicationPage />} />
                {/* Legacy redirects */}
                <Route path="/dashboard" element={<Navigate to="/" replace />} />
                <Route path="/check-ins" element={<Navigate to="/connect" replace />} />
                <Route path="/messages" element={<Navigate to="/connect?tab=messages" replace />} />
                <Route path="/purchase/success" element={<PurchaseSuccessPage />} />
                <Route path="/settings" element={<SettingsPage />} />
              </Route>

              {/* Onboarding (authenticated but no shell) */}
              <Route path="/pets/new/setup" element={
                <ProtectedRoute><PetProfileSetupPage /></ProtectedRoute>
              } />

              {/* Trainer routes */}
              <Route element={<ProtectedRoute requiredRole="trainer"><AppShell /></ProtectedRoute>}>
                <Route path="/trainer/dashboard" element={<TrainerDashboardPage />} />
                <Route path="/trainer" element={<ClientListPage />} />
                <Route path="/trainer/clients/:clientId" element={<ClientDetailPage />} />
                <Route path="/trainer/clients/:clientId/pets/new" element={<TrainerAddPetPage />} />
                <Route path="/trainer/clients/:clientId/pets/:petId" element={<TrainerPetDashboardPage />} />
                <Route path="/trainer/clients/:clientId/enrollments/:enrollmentId" element={<TrainerEnrollmentDetailPage />} />
                <Route path="/trainer/clients/:clientId/enrollments/:enrollmentId/skills/:skillId" element={<TrainerSkillDetailPage />} />
                <Route path="/trainer/assign/:clientId/:petId" element={<AssignHomeworkPage />} />
                <Route path="/trainer/assign/:clientId" element={<AssignHomeworkPage />} />
                <Route path="/trainer/assign-module/:clientId" element={<AssignModulePage />} />
                <Route path="/trainer/clients/:clientId/modules/:assignmentId" element={<TrainerModuleDetailPage />} />
                <Route path="/trainer/clients/:clientId/modules/:assignmentId/lessons/:lessonId" element={<TrainerLessonPage />} />
                <Route path="/trainer/content" element={<ContentLibraryPage />} />
                <Route path="/trainer/content/programs/new" element={<ProgramEditorPage />} />
                <Route path="/trainer/content/programs/:programId/edit" element={<ProgramEditorPage />} />
                <Route path="/trainer/content/programs/:programId" element={<ProgramPreviewPage />} />
                <Route path="/trainer/content/programs/:programId/skills/new" element={<SkillEditorPage />} />
                <Route path="/trainer/content/programs/:programId/skills/:skillId/edit" element={<SkillEditorPage />} />
                <Route path="/trainer/content/programs/:programId/skills/:skillId" element={<SkillPreviewPage />} />
                <Route path="/trainer/content/modules/new" element={<ModuleEditorPage />} />
                <Route path="/trainer/content/modules/:moduleId/edit" element={<ModuleEditorPage />} />
                <Route path="/trainer/content/modules/:moduleId" element={<ModulePreviewPage />} />
                <Route path="/trainer/content/modules/:moduleId/lessons/new" element={<LessonEditorPage />} />
                <Route path="/trainer/content/modules/:moduleId/lessons/:lessonId/edit" element={<LessonEditorPage />} />
                <Route path="/trainer/content/modules/:moduleId/lessons/:lessonId" element={<LessonPreviewPage />} />
                <Route path="/trainer/check-ins" element={<ReviewCheckInsPage />} />
                <Route path="/trainer/messages" element={<ConversationListPage />} />
                <Route path="/trainer/messages/:conversationId" element={<TrainerConversationPage />} />
                <Route path="/trainer/invite" element={<InvitePage />} />
                <Route path="/trainer/settings" element={<SettingsPage />} />
              </Route>

              {/* 404 */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </AuthProvider>
        </ToastProvider>
        </NavigationDirectionProvider>
      </Router>
    </ErrorBoundary>
  );
};

export default App;
